/**
 * The wall, drawn from the real one.
 *
 * The first version had a heart. The application does not have hearts — it has
 * a thumb up and a thumb down, drawn as strokes rather than as 👍/👎 because
 * those render as a different picture on every platform. The glyph paths here
 * are copied from `features/wall/PostCard.tsx` so the front page is showing the
 * control somebody will actually press, not an artist's impression of it.
 *
 * Two posts rather than one, because a wall with a single post on it is not a
 * wall. Both are interactive: press a thumb and it fills and counts, which is
 * the whole behaviour of the real thing.
 */

import { useState } from "react";
import { motion } from "framer-motion";
import { useLocale, useT } from "../../lib/i18n.js";
import "./photoTiles.css";
import "./WallDemo.css";

interface Post {
  who: string;
  face: string;
  name: string;
  /** How long ago, in days — the wording is Intl's job, not ours. */
  days: number;
  text: string;
  kind: string;
  up: number;
  down: number;
  comments: { who: string; face: string; name: string; text: string }[];
}

const POSTS: Post[] = [
  {
    who: "MK",
    face: "1",
    name: "Marta",
    days: 2,
    text: "Nobody told me the last hour of that walk was straight up",
    kind: "landscape",
    up: 3,
    down: 0,
    comments: [
      {
        who: "NW",
        face: "2",
        name: "Nikodem",
        text: "the water was 9 degrees. nine.",
      },
      { who: "PJ", face: "3", name: "Patrycja", text: "and you went in twice" },
    ],
  },
  {
    who: "TB",
    face: "4",
    name: "Tom",
    days: 4,
    text: "found the good coffee. only took until the last morning",
    kind: "stone",
    up: 2,
    down: 1,
    comments: [{ who: "MK", face: "1", name: "Marta", text: "you had four" }],
  },
  {
    who: "PJ",
    face: "3",
    name: "Patrycja",
    days: 5,
    text: "the ferry was late so we swam instead. no regrets",
    kind: "sea",
    up: 4,
    down: 0,
    comments: [
      { who: "TB", face: "4", name: "Tom", text: "best decision of the week" },
      {
        who: "NW",
        face: "2",
        name: "Nikodem",
        text: "i still missed the ferry",
      },
    ],
  },
];

export function WallDemo() {
  const t = useT();
  const { locale } = useLocale();

  /*
   * "2 days ago" was a string in the data, which cannot be translated by a
   * dictionary without writing out every number: Polish says "2 dni temu" but
   * "5 dni temu" and "1 dzień temu", and the rule is the language's, not ours.
   * Intl already holds it.
   */
  const ago = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  return (
    <div className="fc-wall">
      {/*
        A feed with its own scrollbar, at roughly the height of the argument
        beside it. Three posts stacked full-height made the left column twice
        the right one and left half the section empty.

        Focusable and labelled, because a scrollable region a mouse can reach
        has to be reachable by a keyboard too. Scroll chaining is deliberately
        left alone: reaching the end of the feed carries on down the page,
        which is what somebody expects and the reason not to set
        overscroll-behavior here.
      */}
      <div
        className="fc-wall__feed"
        tabIndex={0}
        role="group"
        aria-label={t("Example wall posts")}
      >
        {POSTS.map((post) => (
          <WallPost
            key={post.who}
            post={post}
            when={ago.format(-post.days, "day")}
          />
        ))}
      </div>

      {/* The point of the section, stated by the interface rather than by a
          paragraph: there is nowhere further for this to go. */}
      <p className="fc-wall__closed">
        <LockGlyph />
        {t("Visible to the 4 people in this group. Nobody else, ever.")}
      </p>
    </div>
  );
}

function WallPost({ post, when }: { post: Post; when: string }) {
  const t = useT();
  const [mine, setMine] = useState<"up" | "down" | null>(null);

  const tally = (which: "up" | "down") =>
    post[which] + (mine === which ? 1 : 0);

  const press = (which: "up" | "down") =>
    setMine((current) => (current === which ? null : which));

  return (
    <article className="fc-wall__post">
      <header>
        <span className="fc-wall__avatar" data-face={post.face}>
          {post.who}
        </span>
        <span className="fc-wall__meta">
          <strong>{post.name}</strong>
          <span>
            {t("posted to")} {t("Croatia 2026")} · {when}
          </span>
        </span>
      </header>

      <p className="fc-wall__text">{t(post.text)}</p>

      <div className="fc-wall__photo fc-phototile" data-kind={post.kind} />

      <footer className="fc-wall__actions">
        <motion.button
          type="button"
          data-active={mine === "up" || undefined}
          aria-pressed={mine === "up"}
          aria-label={mine === "up" ? t("Remove your like") : t("Like")}
          onClick={() => press("up")}
          whileTap={{ scale: 0.92 }}
          transition={{ type: "spring", stiffness: 620, damping: 30 }}
        >
          <ThumbUp filled={mine === "up"} />
          <Tally count={tally("up")} />
        </motion.button>

        <motion.button
          type="button"
          data-active={mine === "down" || undefined}
          aria-pressed={mine === "down"}
          aria-label={mine === "down" ? t("Remove your dislike") : t("Dislike")}
          onClick={() => press("down")}
          whileTap={{ scale: 0.92 }}
          transition={{ type: "spring", stiffness: 620, damping: 30 }}
        >
          <ThumbDown filled={mine === "down"} />
          {tally("down") > 0 && <Tally count={tally("down")} />}
        </motion.button>

        <button
          type="button"
          className="fc-wall__commentbtn"
          aria-label={t("Comments")}
        >
          <CommentGlyph />
          <span className="fc-tabular">{post.comments.length}</span>
        </button>
      </footer>

      <ul className="fc-wall__comments">
        {post.comments.map((comment) => (
          <li key={comment.who + comment.text}>
            <span className="fc-wall__avatar" data-face={comment.face}>
              {comment.who}
            </span>
            <span>
              <strong>{comment.name}</strong> {t(comment.text)}
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
}

/**
 * A count that reacts to being changed.
 *
 * Keyed on the number, so React replaces the element and framer-motion runs
 * `initial` again — the vote lands with a small kick rather than the digit
 * silently becoming a different digit. Scale only: if no animation frame ever
 * arrives the number is a fifth too large, which is a blemish rather than a
 * missing count.
 */
function Tally({ count }: { count: number }) {
  return (
    <motion.span
      key={count}
      className="fc-tabular"
      initial={{ scale: 1.2 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", stiffness: 700, damping: 22 }}
    >
      {count}
    </motion.span>
  );
}

/* The three glyphs, copied from the application's own post card. */

function ThumbUp({ filled }: { filled: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      data-filled={filled || undefined}
    >
      <path d="M7 10.5V20H4.5A1.5 1.5 0 0 1 3 18.5v-6.5A1.5 1.5 0 0 1 4.5 10.5zM7 10.5l4.2-7.1a1 1 0 0 1 1.8.5v4.1h4.8a2 2 0 0 1 1.96 2.4l-1.3 6.5A2 2 0 0 1 16.5 20H7z" />
    </svg>
  );
}

function ThumbDown({ filled }: { filled: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      data-filled={filled || undefined}
    >
      <path d="M17 13.5V4h2.5A1.5 1.5 0 0 1 21 5.5V12a1.5 1.5 0 0 1-1.5 1.5zM17 13.5l-4.2 7.1a1 1 0 0 1-1.8-.5v-4.1H6.2a2 2 0 0 1-1.96-2.4l1.3-6.5A2 2 0 0 1 7.5 4H17z" />
    </svg>
  );
}

function CommentGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21 12a8 8 0 0 1-8 8H7l-4 3V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8z" />
    </svg>
  );
}

function LockGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8.5 11V7.5a3.5 3.5 0 0 1 7 0V11" />
    </svg>
  );
}
